import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import {
    Box,
    Card,
    Table,
    TableHead,
    TableRow,
    TableCell,
    TableBody,
    Button,
    MenuItem,
    TextField,
    IconButton,
    Tooltip,
} from "@mui/material";
import { Add, Print, Delete } from "@mui/icons-material";
import { useSnackbar } from "notistack";
import dayjs from "dayjs";

import deliveryReceiptService from "../../services/deliveryReceiptService";
import PageHeader from "../../components/common/PageHeader";
import EmptyState from "../../components/common/EmptyState";
import SkeletonLoader from "../../components/common/SkeletonLoader";
import StatusChip from "../../components/common/StatusChip";
import CreateDeliveryReceiptDialog from "../../components/delivery/CreateDeliveryReceiptDialog";
import { selectStatusOptions, selectKeyForRole } from "../../redux/slices/statusDefinitionsSlice";

const AdminDeliveryReceipts = () => {
    const navigate = useNavigate();
    const { enqueueSnackbar } = useSnackbar();
    const statusOptions = useSelector(selectStatusOptions("deliveryReceipt"));
    const deliveredKey = useSelector(selectKeyForRole("deliveryReceipt", "delivered"));
    const [receipts, setReceipts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [statusFilter, setStatusFilter] = useState("");
    const [createOpen, setCreateOpen] = useState(false);

    const load = async () => {
        setLoading(true);
        try {
            const { data } = await deliveryReceiptService.getAll({ status: statusFilter || undefined });
            setReceipts(data.data.receipts || []);
        } catch {
            enqueueSnackbar("Failed to load delivery receipts", { variant: "error" });
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [statusFilter]);

    const handleDelete = async (e, receipt) => {
        e.stopPropagation();
        if (!window.confirm(`Delete delivery receipt ${receipt.receiptNumber}? This can't be undone — you can recreate it afterward by selecting the shipment/items again.`)) {
            return;
        }
        try {
            await deliveryReceiptService.remove(receipt._id);
            enqueueSnackbar("Delivery receipt deleted", { variant: "success" });
            load();
        } catch (err) {
            enqueueSnackbar(err.response?.data?.message || "Failed to delete delivery receipt", { variant: "error" });
        }
    };

    return (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
            <PageHeader
                title="Delivery Receipts"
                action={
                    <Button variant="contained" startIcon={<Add />} onClick={() => setCreateOpen(true)}>
                        New Receipt
                    </Button>
                }
            />
            <Card>
                <Box sx={{ p: 2, borderBottom: "1px solid", borderColor: "divider" }}>
                    <TextField
                        select
                        size="small"
                        label="Status"
                        value={statusFilter}
                        onChange={(e) => setStatusFilter(e.target.value)}
                        sx={{ width: 200 }}
                    >
                        <MenuItem value="">All</MenuItem>
                        {statusOptions.map((s) => (
                            <MenuItem key={s.value} value={s.value}>
                                {s.label}
                            </MenuItem>
                        ))}
                    </TextField>
                </Box>

                {loading ? (
                    <SkeletonLoader rows={5} />
                ) : receipts.length === 0 ? (
                    <EmptyState
                        icon="🧾"
                        title="No delivery receipts"
                        description="Create a receipt to start assigning deliveries."
                        actionLabel="New Receipt"
                        onAction={() => setCreateOpen(true)}
                    />
                ) : (
                    <Box sx={{ overflowX: "auto" }}>
                        <Table sx={{ minWidth: 800 }}>
                            <TableHead>
                                <TableRow>
                                    <TableCell>Receipt #</TableCell>
                                    <TableCell>Customer</TableCell>
                                    <TableCell>Packages</TableCell>
                                    <TableCell>Rider</TableCell>
                                    <TableCell>Status</TableCell>
                                    <TableCell>Delivered</TableCell>
                                    <TableCell />
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {receipts.map((r) => (
                                    <TableRow key={r._id} hover sx={{ cursor: "pointer" }} onClick={() => r.status === deliveredKey && navigate(`/admin/delivery-receipts/${r.receiptNumber}/print`)}>
                                        <TableCell sx={{ fontFamily: "monospace", fontSize: 12 }}>{r.receiptNumber}</TableCell>
                                        <TableCell>{r.customer?.firstName} {r.customer?.lastName}</TableCell>
                                        <TableCell>{r.items?.length || 0}</TableCell>
                                        <TableCell sx={{ color: "text.secondary" }}>
                                            {r.rider ? `${r.rider.firstName} ${r.rider.lastName}` : "—"}
                                        </TableCell>
                                        <TableCell>
                                            <StatusChip status={r.status} domain="deliveryReceipt" />
                                        </TableCell>
                                        <TableCell sx={{ fontSize: 12, color: "text.secondary" }}>
                                            {r.deliveredAt ? dayjs(r.deliveredAt).format("MMM D, YYYY h:mm A") : "—"}
                                        </TableCell>
                                        <TableCell align="right" sx={{ whiteSpace: "nowrap" }}>
                                            {r.status === deliveredKey ? (
                                                <Button
                                                    size="small"
                                                    startIcon={<Print />}
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        navigate(`/admin/delivery-receipts/${r.receiptNumber}/print`);
                                                    }}
                                                >
                                                    Print
                                                </Button>
                                            ) : (
                                                <Tooltip title="Delete receipt">
                                                    <IconButton size="small" onClick={(e) => handleDelete(e, r)}>
                                                        <Delete fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                            )}
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </Box>
                )}
            </Card>

            <CreateDeliveryReceiptDialog open={createOpen} onClose={() => setCreateOpen(false)} onCreated={load} />
        </Box>
    );
};

export default AdminDeliveryReceipts;
